import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CheckCircle2, Rocket } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { countryOptions, defaultCountryCurrency } from '@/lib/quoteCountries';

const API_URL = process.env.REACT_APP_API_URL;

/**
 * QuoteSetup
 * ----------
 * Post-registration wizard. Guards against being visited more than once:
 * on mount it checks `/api/quote/setup-status` and redirects to `/quotes`
 * if the tenant has already completed setup.
 */
export const QuoteSetup = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('quotes');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [companyName, setCompanyName] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [needsCountrySelection, setNeedsCountrySelection] = useState(false);
  const [currency, setCurrency] = useState('TRY');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [industries, setIndustries] = useState([]);
  const [industry, setIndustry] = useState('');
  const [seedCatalog, setSeedCatalog] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [status, ind, profile] = await Promise.all([
          axios.get(`${API_URL}/api/quote/setup-status`),
          axios.get(`${API_URL}/api/quote/industries`),
          axios.get(`${API_URL}/api/quote/profile`),
        ]);
        if (status.data.setup_completed) {
          navigate('/quotes', { replace: true });
          return;
        }
        setIndustries(ind.data?.industries || []);
        const registeredCountry = profile.data?.country_code || '';
        setNeedsCountrySelection(registeredCountry === 'OTHER');
        setCountryCode(registeredCountry === 'OTHER' ? '' : registeredCountry);
        setCurrency(profile.data?.currency || defaultCountryCurrency(registeredCountry));
      } catch (err) {
        toast.error(err.response?.data?.detail || t('setup.error'));
        // Do not overwrite the registered billing country when it cannot be loaded.
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const onCountryChange = (code) => {
    setCountryCode(code);
    if (code) setCurrency(defaultCountryCurrency(code));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!companyName.trim()) {
      toast.error(t('setup.companyRequired'));
      return;
    }
    if (!countryCode) {
      toast.error(t('setup.countryRequired'));
      return;
    }
    if (!industry) {
      toast.error(t('setup.industryRequired'));
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/quote/setup`, {
        company_name: companyName.trim(),
        country_code: countryCode,
        currency,
        company_phone: phone || null,
        company_address: address || null,
        industry,
        seed_catalog: seedCatalog,
      });
      toast.success(
        res.data?.master_catalog_enabled
          ? t('setup.successCatalogAssigned')
          : t('setup.successNoCatalog'),
      );
      navigate('/quotes/new', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('setup.error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] p-4">
        <p className="text-sm text-[#595959]">{t('common.loading')}</p>
      </div>
    );
  }

  const lang = i18n.resolvedLanguage || i18n.language || 'tr';

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#F9F8F6] to-[#EBE9E4] p-4"
      data-testid="quote-setup-page"
    >
      <div className="w-full max-w-lg space-y-5">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium mb-3">
            <CheckCircle2 className="w-3.5 h-3.5" /> {t('setup.badge')}
          </div>
          <h1 className="text-2xl font-bold text-[#1A1A2E]" data-testid="quote-setup-title">
            {t('setup.title')}
          </h1>
          <p
            className="text-sm text-[#595959] mt-1"
            // Subtitle contains an inline <b> tag — safe because it's a
            // static, translator-controlled string, not user input.
            dangerouslySetInnerHTML={{ __html: t('setup.subtitle') }}
          />
        </div>

        <Card className="border border-[#E5E5E5] shadow-lg">
          <CardContent className="p-5">
            <form onSubmit={submit} className="space-y-4" data-testid="quote-setup-form">
              <div className="space-y-1.5">
                <Label className="text-sm">{t('setup.companyName')} <span className="text-red-500">*</span></Label>
                <Input
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder={t('setup.companyPlaceholder')}
                  required
                  minLength={1}
                  maxLength={200}
                  data-testid="quote-setup-company-input"
                  className="h-11"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {needsCountrySelection && (
                  <div className="space-y-1.5">
                    <Label className="text-sm">{t('setup.country')}</Label>
                    <select value={countryCode} onChange={(e) => onCountryChange(e.target.value)} required
                      className="h-11 w-full border border-[#E5E5E5] rounded-md px-3 text-sm bg-white"
                      data-testid="quote-setup-country-select">
                      <option value="">{t('register.countryPlaceholder')}</option>
                      {countryOptions(lang.split('-')[0]).map((c) => (
                        <option key={c.code} value={c.code}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label className="text-sm">{t('setup.currency')}</Label>
                  <Input
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                    maxLength={3}
                    data-testid="quote-setup-currency-input"
                    className="h-11"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm">{t('setup.phone')} <span className="text-[#94A3B8] text-xs">{t('setup.phoneOptional')}</span></Label>
                <Input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t('setup.phonePlaceholder')}
                  maxLength={50}
                  data-testid="quote-setup-phone-input"
                  className="h-11"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm">{t('setup.address')} <span className="text-[#94A3B8] text-xs">{t('setup.addressOptional')}</span></Label>
                <Textarea
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder={t('setup.addressPlaceholder')}
                  rows={2}
                  maxLength={1000}
                  data-testid="quote-setup-address-input"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm">{t('setup.industry')} <span className="text-red-500">*</span></Label>
                <select
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  required
                  className="h-11 w-full border border-[#E5E5E5] rounded-md px-3 text-sm bg-white"
                  data-testid="quote-setup-industry-select"
                >
                  <option value="">{t('setup.industryPlaceholder')}</option>
                  {industries.map((ind) => (
                    <option key={ind.key} value={ind.key}>
                      {(lang || '').startsWith('en') ? (ind.name_en || ind.name) : ind.name}
                      {ind.item_count > 0 ? ` (${ind.item_count} ${t('setup.readyItems')})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <label className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={seedCatalog}
                  onChange={(e) => setSeedCatalog(e.target.checked)}
                  className="mt-0.5"
                  data-testid="quote-setup-seed-catalog"
                />
                <span className="text-sm text-[#1A1A2E]">
                  <b>{t('setup.seedCatalog')}</b>{' '}
                  <span className="text-[#64748B]">{t('setup.seedCatalogHint')}</span>
                </span>
              </label>

              <Button
                type="submit"
                className="w-full h-11 text-white gap-2 bg-[#2563EB] hover:bg-[#1E4ED8]"
                disabled={saving}
                data-testid="quote-setup-submit"
              >
                {saving ? t('setup.submitting') : (<><Rocket className="w-4 h-4" /> {t('setup.submit')}</>)}
              </Button>

              <p
                className="text-[11px] text-center text-[#94A3B8] pt-1"
                dangerouslySetInnerHTML={{ __html: t('setup.defaultsNote') }}
              />
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default QuoteSetup;
